import { useState, useEffect, useMemo } from 'react';
import { useProfiles } from '../hooks/useProfiles';
import { useAuthContext } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { API_URL } from '../lib/api-client';
import { BlurredPhotoCard } from './BlurredPhotoCard';
import { logger } from '../utils/logger';
import { usePhotoBlur } from '../hooks/usePhotoBlur';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Input } from './ui/input';
import ModernFilterSidebar from './ModernFilterSidebar';
import ProfileListSection from './ProfileListSection';
import { ProfileSidebarSections } from './ProfileSidebarSections';
import ViewAllProfilesPage from './ViewAllProfilesPage';
import { ProfileCard } from './ProfileCard';
import axios from 'axios';
import { 
  Heart, 
  X, 
  MapPin, 
  Briefcase, 
  GraduationCap, 
  Users, 
  MessageCircle, 
  Eye, 
  ChevronDown,
  Crown,
  Sparkles,
  TrendingUp,
  SlidersHorizontal,
  Grid3x3,
  List,
  Bookmark,
  ArrowLeft,
  ChevronRight,
  Activity,
  ChevronLeft,
  Star
} from 'lucide-react';

import { themes, useTheme } from './ThemeProvider';

interface DesktopMatchesLayoutProps {
  onViewProfile: (profile: any) => void;
  initialFilter?: 'similar' | 'suggested';
}

type ViewMode = 'grid' | 'list';
type SortBy = 'recent' | 'active' | 'premium' | 'nearby';

export default function DesktopMatchesLayout({ onViewProfile, initialFilter }: DesktopMatchesLayoutProps) {
  const [selectedFilter, setSelectedFilter] = useState<string>('discovery');
  const [showFilters, setShowFilters] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [photoIndices, setPhotoIndices] = useState<Record<string, number>>({});
  const [autoScrollIntervals, setAutoScrollIntervals] = useState<Record<string, any>>({});
  const [shortlistedProfiles, setShortlistedProfiles] = useState<Set<string>>(new Set());
  const [loadingInterests, setLoadingInterests] = useState<Set<string>>(new Set());
  const [sentInterests, setSentInterests] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [initialCategoryProfiles, setInitialCategoryProfiles] = useState<any[]>([]);
  const [suggestedProfiles, setSuggestedProfiles] = useState<any[]>([]);
  const [featuredProfiles, setFeaturedProfiles] = useState<any[]>([]);
  const [newMatchesProfiles, setNewMatchesProfiles] = useState<any[]>([]);
  const [moreMatchesProfiles, setMoreMatchesProfiles] = useState<any[]>([]);
  const [showViewAllPage, setShowViewAllPage] = useState<{
    show: boolean;
    title: string;
    filterType: 'new' | 'more' | 'similar' | 'suggested';
  }>({ show: true, title: 'More Matches', filterType: 'more' });
  
  const { user } = useAuthContext();
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  
  // Use photo blur hook at component level
  const { currentUserPhotoCount, getPhotoBlurState } = usePhotoBlur();

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      // Clear all intervals when component unmounts
      Object.values(autoScrollIntervals).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, [autoScrollIntervals]);

  // Fetch current user's full profile data to get photo count
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !user) return;

        logger.log('🔄 Fetching user profile from /profiles/me...');

        const response = await fetch(`${API_URL}/profiles/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          logger.log('✅ FULL API RESPONSE from /profiles/me:', data);
          logger.log('✅ data.data:', data.data);
          logger.log('✅ data.data.photos:', data.data?.photos);
          
          // The backend returns: { success: true, data: profile, hasPendingChanges }
          // So the profile is at data.data
          const profileData = data.data;
          logger.log('✅ Extracted profile data:', profileData);
          logger.log('✅ Photos array:', profileData?.photos);
          logger.log('✅ Photos count:', profileData?.photos?.length);
          
          setCurrentUserProfile(profileData);
        } else {
          logger.error('❌ Failed to fetch profile. Status:', response.status);
          const errorText = await response.text();
          logger.error('❌ Error response:', errorText);
        }
      } catch (error) {
        logger.error('❌ Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Handle initial filter (e.g., when coming from "View All Similar")
  useEffect(() => {
    if (initialFilter === 'similar') {
      setShowViewAllPage({
        show: true,
        title: 'Similar Profiles',
        filterType: 'similar'
      });
    } else if (initialFilter === 'suggested') {
      setShowViewAllPage({
        show: true,
        title: 'Suggested For You',
        filterType: 'suggested'
      });
    }
  }, [initialFilter]);

  // Fetch shortlisted profiles on mount
  useEffect(() => {
    const fetchShortlistedProfiles = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${API_URL}/interactions/sent?type=shortlist`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const interactions = data.data?.interactions || data.data || [];
            const shortlistedIds = interactions
              .filter((int: any) => int.status === 'shortlist')
              .map((int: any) => int.toProfile?._id || int.toProfile || int.toUser?._id || int.toUser)
              .filter(Boolean);
            setShortlistedProfiles(new Set(shortlistedIds));
          }
        }
      } catch (error) {
        logger.error('❌ Failed to fetch shortlisted profiles:', error);
      }
    };

    fetchShortlistedProfiles();
  }, []);

  // Filter states
  const [ageRange, setAgeRange] = useState([21, 35]);
  const [heightRange, setHeightRange] = useState([4.5, 6.5]);
  const [selectedReligion, setSelectedReligion] = useState('all');
  const [selectedCaste, setSelectedCaste] = useState('all');
  const [selectedEducation, setSelectedEducation] = useState('all');
  const [selectedOccupation, setSelectedOccupation] = useState('all');
  const [selectedMaritalStatus, setSelectedMaritalStatus] = useState('all');
  const [selectedMotherTongue, setSelectedMotherTongue] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedState, setSelectedState] = useState('all');

  const { profiles, loading, updateFilters, resetFilters, pagination, nextPage, prevPage, goToPage } = useProfiles();

  // Safety check for pagination - map Redux pagination to component expectations
  const safePagination = pagination ? {
    page: pagination.page || 1,
    limit: pagination.limit || 20,
    total: pagination.total || 0,
    pages: pagination.pages || 0,
    totalPages: pagination.pages || 0, // Map pages to totalPages
    currentPage: pagination.page || 1 // Map page to currentPage
  } : { 
    page: 1, 
    limit: 20, 
    total: 0, 
    pages: 0, 
    totalPages: 0, 
    currentPage: 1 
  };

  // Debug pagination data
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Pagination Debug:', {
        pagination,
        safePagination,
        showPagination: safePagination.totalPages > 1
      });
    }
  }, [pagination, safePagination.totalPages]);

  // Fetch suggested, featured, new matches, and more matches profiles
  useEffect(() => {
    fetchSuggestedProfiles();
    fetchFeaturedProfiles();
    fetchNewMatchesProfiles();
    fetchMoreMatchesProfiles();
  }, []);

  const fetchSuggestedProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logger.log('⚠️ No token found for suggested profiles');
        return;
      }

      logger.log('🔍 Fetching suggested profiles...');
      // Use the new suggested endpoint that filters by age +1/-1 year
      const response = await axios.get(`${API_URL}/profiles/suggested?limit=6`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      logger.log('📊 Suggested profiles response:', response.data);

      if (response.data.success && response.data.data) {
        const profilesArray = response.data.data.profiles || [];
        logger.log(`✅ Setting ${profilesArray.length} suggested profiles`);
        setSuggestedProfiles(profilesArray);
      } else {
        logger.log('⚠️ No suggested profiles data in response');
        setSuggestedProfiles([]);
      }
    } catch (error) {
      logger.error('❌ Error fetching suggested profiles:', error);
      if (error.response) {
        logger.error('Response error:', error.response.data);
      }
      setSuggestedProfiles([]);
    }
  };

  const fetchFeaturedProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Use the new featured endpoint that only returns premium members
      const response = await axios.get(`${API_URL}/profiles/featured?limit=4`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        const profilesArray = response.data.data.profiles || [];
        setFeaturedProfiles(profilesArray);
      }
    } catch (error) {
      logger.error('Error fetching featured profiles:', error);
      setFeaturedProfiles([]);
    }
  };

  const fetchNewMatchesProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Fetch profiles created in last 7 days using filterType=new
      const response = await axios.get(`${API_URL}/profiles?filterType=new&limit=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        const profilesArray = response.data.data.profiles || [];
        setNewMatchesProfiles(profilesArray);
      }
    } catch (error) {
      logger.error('Error fetching new matches profiles:', error);
      setNewMatchesProfiles([]);
    }
  };

  const fetchMoreMatchesProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Use discovery endpoint with city->state->age priority sorting
      const response = await axios.get(`${API_URL}/profiles/discovery?limit=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        const profilesArray = response.data.data.profiles || [];
        setMoreMatchesProfiles(profilesArray);
      }
    } catch (error) {
      logger.error('Error fetching more matches profiles:', error);
      setMoreMatchesProfiles([]);
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    const filters: any = {};
    
    if (ageRange[0] !== 21 || ageRange[1] !== 35) {
      filters.minAge = ageRange[0];
      filters.maxAge = ageRange[1];
    }
    
    if (heightRange[0] !== 4.5 || heightRange[1] !== 6.5) {
      filters.minHeight = Math.round(heightRange[0] * 30.48);
      filters.maxHeight = Math.round(heightRange[1] * 30.48);
    }
    
    if (selectedReligion !== 'all') filters.religion = selectedReligion;
    if (selectedCaste !== 'all') filters.caste = selectedCaste;
    if (selectedEducation !== 'all') filters.education = selectedEducation;
    if (selectedOccupation !== 'all') filters.occupation = selectedOccupation;
    if (selectedMaritalStatus !== 'all') filters.maritalStatus = selectedMaritalStatus;
    if (selectedMotherTongue !== 'all') filters.motherTongue = selectedMotherTongue;
    if (selectedCity !== 'all') filters.city = selectedCity;
    if (selectedState !== 'all') filters.state = selectedState;
    
    // Add active filter type if set
    if (selectedFilter !== 'all') {
      filters.filterType = selectedFilter;
    }

    // Development logging
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 FILTER DEBUG - User Selections:');
      console.log('   - Age Range:', ageRange);
      console.log('   - Height Range:', heightRange);
      console.log('   - Religion:', selectedReligion);
      console.log('   - Caste:', selectedCaste);
      console.log('   - Education:', selectedEducation);
      console.log('   - Occupation:', selectedOccupation);
      console.log('   - Marital Status:', selectedMaritalStatus);
      console.log('   - Mother Tongue:', selectedMotherTongue);
      console.log('   - City:', selectedCity);
      console.log('   - State:', selectedState);
      console.log('   - Active Filter:', selectedFilter);
      console.log('🔍 FILTER DEBUG - Transformed Filters:', filters);
    }

    updateFilters(filters);
    
    const filterCount = Object.keys(filters).length;
    if (filterCount > 0) {
      toast.success('Filters applied successfully!', {
        description: `${filterCount} filter(s) active - searching...`
      });
    } else {
      toast.info('All filters cleared', {
        description: 'Showing all profiles'
      });
    }
  };

  // View all profiles of a specific category
  const handleViewAllCategory = (category: 'premium' | 'new' | 'verified' | 'nearby' | 'recently-active' | 'all') => {
    // For 'new' category (New Matches), show ViewAllProfilesPage
    if (category === 'new') {
      setShowViewAllPage({
        show: true,
        title: 'New Matches',
        filterType: 'new'
      });
      return;
    }
    
    // For 'all' category (More Matches), show ViewAllProfilesPage
    if (category === 'all') {
      setShowViewAllPage({
        show: true,
        title: 'More Matches',
        filterType: 'more'
      });
      return;
    }
    
    // For other categories, use the old behavior
    // Reset UI filters first
    setAgeRange([21, 35]);
    setHeightRange([4.5, 6.5]);
    setSelectedReligion('all');
    setSelectedCaste('all');
    setSelectedEducation('all');
    setSelectedOccupation('all');
    setSelectedMaritalStatus('all');
    setSelectedMotherTongue('all');
    setSelectedCity('all');
    setSelectedState('all');
    
    // For specific categories, save initial profiles and apply filter
    let categoryProfiles: any[] = [];
    if (category === 'premium') {
      categoryProfiles = [...categorizedProfiles.premium];
    } else if (category === 'recently-active') {
      categoryProfiles = [...categorizedProfiles.recent];
    }
    setInitialCategoryProfiles(categoryProfiles);
    
    setSelectedFilter(category);
    const filters: any = { filterType: category, page: 1, limit: 50 };
    
    // Development logging
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 CATEGORY VIEW - Switching to category:', category);
      console.log('   - Current profiles count:', profiles.length);
      console.log('   - Filters being applied:', filters);
    }
    
    updateFilters(filters);
    
    const categoryNames = {
      premium: 'Premium Members',
      verified: 'Verified Profiles',
      nearby: 'Nearby Matches'
    };
    
    toast.info(`Showing ${categoryNames[category]}`, {
      description: 'Loading all profiles in this category...'
    });
  };

  // Back to discovery view (home with categorized sections)
  const handleBackToAll = () => {
    setSelectedFilter('discovery');
    setInitialCategoryProfiles([]); // Clear initial profiles
    resetFilters(); // Use Redux resetFilters instead of handleResetFilters to avoid double toast
    toast.info('Back to Discover');
  };

  const handleResetFilters = () => {
    // Reset UI state
    setAgeRange([21, 35]);
    setHeightRange([4.5, 6.5]);
    setSelectedReligion('all');
    setSelectedCaste('all');
    setSelectedEducation('all');
    setSelectedOccupation('all');
    setSelectedMaritalStatus('all');
    setSelectedMotherTongue('all');
    setSelectedCity('all');
    setSelectedState('all');
    setSelectedFilter('all');
    
    // Reset filters in Redux and fetch all profiles
    resetFilters();
    
    toast.info('All filters cleared', {
      description: 'Showing all profiles'
    });
  };

  // Handle send interest
  const handleSendInterest = async (profileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to send interest');
        return;
      }

      // Set loading state
      setLoadingInterests(prev => new Set([...prev, profileId]));

      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          profileId,
          type: 'interest'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Add to sent interests
        setSentInterests(prev => new Set([...prev, profileId]));
        
        if (data.data?.isMatch) {
          toast.success("It's a Match! 💕", {
            description: 'You can now start chatting!',
            duration: 5000
          });
        } else {
          toast.success('Interest sent successfully! 💌');
        }
      } else {
        toast.error(data.message || 'Failed to send interest');
      }
    } catch (error) {
      toast.error('Failed to send interest');
    } finally {
      // Remove loading state
      setLoadingInterests(prev => {
        const newSet = new Set(prev);
        newSet.delete(profileId);
        return newSet;
      });
    }
  };

  // Handle send super interest
  const handleSendSuperInterest = async (profileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to send super interest');
        return;
      }

      const { removeProfile } = await import('../store/slices/discoverySlice');
      const { store } = await import('../store/store');
      store.dispatch(removeProfile(profileId));

      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          profileId,
          type: 'superlike'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (data.data?.isMatch) {
          toast.success("It's a Match! 💕", {
            description: 'You can now start chatting!',
            duration: 5000
          });
        } else {
          toast.success('Super Interest sent successfully! ⭐', {
            description: 'Your profile will stand out!'
          });
        }
      } else {
        toast.error(data.message || 'Failed to send super interest');
      }
    } catch (error) {
      toast.error('Failed to send super interest');
    }
  };

  // Check if user has super interest credits
  const hasSuperInterestCredits = useMemo(() => {
    // Check reward credits first
    const rewardSuperInterests = (user as any)?.rewardCredits?.superInterests || 0;
    if (rewardSuperInterests > 0) {
      return true;
    }
    
    // Then check plan-based credits
    const totalCredits = (user as any)?.subscription?.features?.totalSuperInterests || 0;
    const usedCount = (user as any)?.totalSuperInterestsUsed || 0;
    
    // Calculate remaining credits (one-time allocation, not daily)
    const remainingCredits = totalCredits - usedCount;
    
    return remainingCredits > 0;
  }, [user]);

  const handleShortlist = async (profileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to shortlist');
        return;
      }

      const isShortlisted = shortlistedProfiles.has(profileId);

      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          profileId,
          type: 'shortlist'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (isShortlisted) {
          // Remove from shortlist
          setShortlistedProfiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(profileId);
            return newSet;
          });
          toast.success('Removed from shortlist!');
        } else {
          // Add to shortlist
          setShortlistedProfiles(prev => new Set([...prev, profileId]));
          toast.success('Added to shortlist!');
        }
      } else {
        toast.error(data.message || 'Failed to shortlist');
      }
    } catch (error) {
      toast.error('Failed to shortlist');
    }
  };

  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return 25;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const heightToFeetInches = (height: any) => {
    if (typeof height === 'string' && height.includes("'")) return height;
    if (typeof height === 'number') {
      const totalInches = height / 2.54;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return `${feet}'${inches}"`;
    }
    return "5'5\"";
  };

  const getProfilePhoto = (profile: any) => {
    // Priority 1: Direct profilePhoto URL (S3 URL from DB)
    if (profile?.profilePhoto) {
      return profile.profilePhoto;
    }
    
    // Priority 2: Photos array with url objects
    if (profile?.photos?.length > 0) {
      // Check if photos[0] is an object with url field
      if (typeof profile.photos[0] === 'object' && profile.photos[0]?.url) {
        return profile.photos[0].url;
      }
      // Fallback: if photos[0] is a string (old format)
      if (typeof profile.photos[0] === 'string') {
        // Check if it's already a full URL
        if (profile.photos[0].startsWith('http')) {
          return profile.photos[0];
        }
        return `${API_URL.replace('/api', '')}/uploads/${profile.photos[0]}`;
      }
    }
    
    // Priority 3: Check populated userId photos
    if (profile?.userId?.profilePhoto) {
      return profile.userId.profilePhoto;
    }
    
    if (profile?.userId?.photos?.length > 0) {
      if (typeof profile.userId.photos[0] === 'object' && profile.userId.photos[0]?.url) {
        return profile.userId.photos[0].url;
      }
      if (typeof profile.userId.photos[0] === 'string') {
        if (profile.userId.photos[0].startsWith('http')) {
          return profile.userId.photos[0];
        }
        return `${API_URL.replace('/api', '')}/uploads/${profile.userId.photos[0]}`;
      }
    }
    
    return null;
  };

  const getInitials = (profile: any) => {
    // Try to get name from various possible locations
    const firstName = profile?.firstName || 
                      profile?.basicInfo?.firstName || 
                      profile?.userId?.firstName || 
                      profile?.name?.split(' ')[0] || 
                      '';
    const lastName = profile?.lastName || 
                     profile?.basicInfo?.lastName || 
                     profile?.userId?.lastName || 
                     profile?.name?.split(' ')[1] || 
                     '';
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    return 'U';
  };
  
  const getFirstName = (profile: any) => {
    // Try to get first name from various possible locations
    return profile?.firstName || 
           profile?.basicInfo?.firstName || 
           profile?.userId?.firstName || 
           profile?.name?.split(' ')[0] || 
           'User';
  };

  // Helper to safely render education
  const getEducationDisplay = (profile: any) => {
    const education = profile?.education;
    if (!education) return 'Not specified';
    if (typeof education === 'string') return education;
    if (typeof education === 'object') {
      const parts: string[] = [];
      if (education.level) parts.push(education.level);
      if (education.field) parts.push(education.field);
      if (education.institution) parts.push(education.institution);
      return parts.length > 0 ? parts.join(' - ') : 'Not specified';
    }
    return 'Not specified';
  };

  // Categorize profiles
  const categorizedProfiles = useMemo(() => {
    const premium: any[] = [];
    
    // When viewing a specific category, merge initial profiles with API results
    let all: any[] = [];
    
    if (selectedFilter !== 'all' && initialCategoryProfiles.length > 0) {
      // Start with initial profiles (from discovery tab)
      const initialProfileIds = new Set(initialCategoryProfiles.map(p => p._id || p.id));
      
      // Add API profiles that aren't already in initial list
      const newProfilesFromAPI = (profiles || []).filter(p => {
        const profileId = p._id || p.id;
        return !initialProfileIds.has(profileId);
      });
      
      // Merge: initial profiles first, then new ones from API
      all = [...initialCategoryProfiles, ...newProfilesFromAPI];
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 MERGED PROFILES:', {
          initialCount: initialCategoryProfiles.length,
          newFromAPI: newProfilesFromAPI.length,
          totalMerged: all.length
        });
      }
    } else {
      // For 'all' filter, just use API profiles
      all = profiles || [];
    }

    all.forEach(profile => {
      if (profile.isPremium || profile.activePlan) {
        premium.push(profile);
      }
    });

    // Use separate state for new matches and more matches instead of filtering from 'all'
    return { 
      premium, 
      newMatches: newMatchesProfiles,  // From /profiles?filterType=new
      all: moreMatchesProfiles  // From /discovery endpoint with city->state->age priority
    };
  }, [profiles, selectedFilter, initialCategoryProfiles, newMatchesProfiles, moreMatchesProfiles]);

  const renderProfileCard = (profile: any, isPremium = false) => {
    const age = calculateAge(profile?.dateOfBirth || profile?.basicInfo?.dateOfBirth);
    const height = heightToFeetInches(profile?.height || profile?.physicalAttributes?.height);
    const photo = getProfilePhoto(profile);
    const initials = getInitials(profile);
    const firstName = getFirstName(profile);
    const profileId = profile?._id || profile?.userId?._id || profile?.id;

    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [DesktopMatchesLayout] Rendering profile card:', {
        profileId,
        profile_id: profile?._id,
        userId: profile?.userId,
        userId_id: profile?.userId?._id,
        profile_id_field: profile?.id,
        firstName,
        fullProfile: profile
      });
    }

    // Get all photos for this profile
    const profilePhotos = [];
    if (profile?.photos && Array.isArray(profile.photos) && profile.photos.length > 0) {
      profile.photos.forEach((p: any) => {
        if (typeof p === 'object' && p?.url) {
          profilePhotos.push(p.url);
        } else if (typeof p === 'string' && p.trim()) {
          profilePhotos.push(p.startsWith('http') ? p : `${API_URL.replace('/api', '')}/uploads/${p}`);
        }
      });
    }
    
    // If no photos in array, try single photo
    if (profilePhotos.length === 0 && photo) {
      profilePhotos.push(photo);
    }
    
    // Current photo index for this profile
    const currentPhotoIndex = photoIndices[profileId] || 0;
    const currentPhoto = profilePhotos[currentPhotoIndex] || photo;
    const hasMultiplePhotos = profilePhotos.length > 1;

    // Auto-scroll handler
    const startAutoScroll = (profileId: string) => {
      if (hasMultiplePhotos && !autoScrollIntervals[profileId]) {
        const interval = setInterval(() => {
          setPhotoIndices(prev => {
            const currentIndex = prev[profileId] || 0;
            const nextIndex = (currentIndex + 1) % profilePhotos.length;
            return { ...prev, [profileId]: nextIndex };
          });
        }, 3000); // Change photo every 3 seconds
        
        setAutoScrollIntervals(prev => ({ ...prev, [profileId]: interval }));
      }
    };

    const stopAutoScroll = (profileId: string) => {
      if (autoScrollIntervals[profileId]) {
        clearInterval(autoScrollIntervals[profileId]);
        setAutoScrollIntervals(prev => {
          const newIntervals = { ...prev };
          delete newIntervals[profileId];
          return newIntervals;
        });
      }
    };

    const handlePhotoNav = (profileId: string, direction: 'prev' | 'next', e: React.MouseEvent) => {
      e.stopPropagation();
      setPhotoIndices(prev => {
        const currentIndex = prev[profileId] || 0;
        let nextIndex;
        if (direction === 'next') {
          nextIndex = (currentIndex + 1) % profilePhotos.length;
        } else {
          nextIndex = (currentIndex - 1 + profilePhotos.length) % profilePhotos.length;
        }
        return { ...prev, [profileId]: nextIndex };
      });
    };

    // Calculate if photo should be blurred
    const photoVisibility = getPhotoBlurState();
    const shouldBlurPhoto = !photoVisibility.canSeePhotos;
    
    // For scrolled photos: blur photos beyond (user's upload count + 1 free photo)
    // If user has 0 photos, can see 1 photo (index 0); if user has 1 photo, can see 2 photos (index 0-1), etc.
    const shouldBlurCurrentPhoto = currentPhotoIndex >= currentUserPhotoCount + 1;

    const displayName = profile?.displayName || profile?.name || profile?.basicInfo?.fullName || firstName;

    if (viewMode === 'grid') {
      return (
        <ProfileCard 
          key={profileId}
          profile={profile}
          onViewProfile={onViewProfile}
          onSendInterest={(pid, e) => handleSendInterest(pid, e)}
          onShortlist={(pid, e) => handleShortlist(pid, e)}
          isShortlisted={shortlistedProfiles.has(profileId)}
          isSentInterest={sentInterests.has(profileId)}
          isLoadingInterest={loadingInterests.has(profileId)}
        />
      );
    } else {
      // List View
      return (
        <Card 
          key={profileId}
          className="hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden"
          onClick={() => onViewProfile(profile)}
          onMouseEnter={() => startAutoScroll(profileId)}
          onMouseLeave={() => stopAutoScroll(profileId)}
        >
          <div className="flex gap-4 p-4">
            {/* Profile Image */}
            <div className="w-32 h-40 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden relative">
              {currentPhoto ? (
                <>
                  <ImageWithFallback
                    src={currentPhoto}
                    alt={firstName}
                    className="w-full h-full object-cover"
                    style={shouldBlurCurrentPhoto ? { filter: 'blur(20px)' } : undefined}
                  />
                  {shouldBlurCurrentPhoto && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                      <div className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg text-center">
                        <p className="text-xs font-medium text-gray-900">
                          {currentUserPhotoCount === 0 ? 'Upload 1 photo' : `Upload ${currentPhotoIndex - currentUserPhotoCount} more`}
                        </p>
                        <p className="text-xs text-gray-600">to view more photos</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-100 to-purple-100">
                  <span className="text-4xl text-gray-400">{initials}</span>
                </div>
              )}
              
              {(isPremium || profile.isPremium || profile.activePlan) && (
                <div className="absolute top-2 left-2">
                  <Badge className="bg-yellow-500 text-white border-0 text-xs">
                    <Crown className="size-3" />
                  </Badge>
                </div>
              )}
            </div>

            {/* Profile Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-xl mb-1">{firstName}</h3>
                  <p className="text-gray-600">{age} yrs, {height}</p>
                </div>
                {profile.isOnline && (
                  <Badge className="bg-green-500 text-white border-0">
                    <span className="size-2 bg-white rounded-full mr-1.5 animate-pulse"></span>
                    Online
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                {profile?.religion && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="size-4 text-gray-400" />
                    <span>{profile.religion}</span>
                  </div>
                )}
                {profile?.education && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <GraduationCap className="size-4 text-gray-400" />
                    <span className="truncate">{getEducationDisplay(profile)}</span>
                  </div>
                )}
                {profile?.occupation && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Briefcase className="size-4 text-gray-400" />
                    <span className="truncate">{profile.occupation}</span>
                  </div>
                )}
                {profile?.city && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="size-4 text-gray-400" />
                    <span>{profile.city}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-pink-500 hover:bg-pink-600 text-white disabled:opacity-60"
                  onClick={(e) => handleSendInterest(profileId, e)}
                  disabled={loadingInterests.has(profileId) || sentInterests.has(profileId)}
                >
                  {loadingInterests.has(profileId) ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  ) : sentInterests.has(profileId) ? (
                    <>Interest Sent</>
                  ) : (
                    <>
                      <Heart className="size-4 mr-1" />
                      Send Interest
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewProfile(profile);
                  }}
                >
                  <Eye className="size-4 mr-1" />
                  View Profile
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => handleShortlist(profileId, e)}
                >
                  <Bookmark className="size-5" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      );
    }
  };

  // Show ViewAllProfilesPage if requested
  if (showViewAllPage.show) {
    return (
      <ViewAllProfilesPage
        title={showViewAllPage.title}
        filterType={showViewAllPage.filterType}
        onBack={() => setShowViewAllPage({ show: false, title: '', filterType: 'new' })}
        onViewProfile={onViewProfile}
      />
    );
  }

  return (
    <div className="flex h-full bg-gradient-to-br from-gray-50 via-pink-50/20 to-purple-50/20">
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Header with View Toggle */}
        <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <Button
                    size="sm"
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    onClick={() => setViewMode('grid')}
                    className="px-3"
                  >
                    <Grid3x3 className="size-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    onClick={() => setViewMode('list')}
                    className="px-3"
                  >
                    <List className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="max-w-7xl mx-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading profiles...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Premium Matches */}
              <ProfileListSection
                title="Premium Matches"
                icon={Crown}
                iconColor="text-yellow-500"
                badgeColor="bg-yellow-500 text-white"
                buttonColor="bg-yellow-500 hover:bg-yellow-600 text-white"
                profiles={selectedFilter === 'discovery' ? categorizedProfiles.premium : []}
                viewMode={viewMode}
                renderProfileCard={renderProfileCard}
                onViewAll={() => handleViewAllCategory('premium')}
                viewAllText="View All Premium"
                maxPreview={8}
                showPremiumBadge={true}
              />

              {/* New Matches */}
              <ProfileListSection
                title="New Matches"
                icon={Sparkles}
                iconColor="text-pink-500"
                badgeColor="bg-pink-500 text-white"
                buttonColor="bg-pink-500 hover:bg-pink-600 text-white"
                profiles={selectedFilter === 'discovery' ? categorizedProfiles.newMatches : []}
                viewMode={viewMode}
                renderProfileCard={renderProfileCard}
                onViewAll={() => handleViewAllCategory('new')}
                viewAllText="View All New"
                maxPreview={8}
              />

              {/* More Matches Preview - Show on discovery homepage */}
              {selectedFilter === 'discovery' && categorizedProfiles.all.length > 0 && (
                <ProfileListSection
                  title="More Matches"
                  icon={Users}
                  iconColor="text-blue-500"
                  badgeColor="bg-blue-500 text-white"
                  buttonColor="bg-blue-500 hover:bg-blue-600 text-white"
                  profiles={categorizedProfiles.all.slice(0, 8)}
                  viewMode={viewMode}
                  renderProfileCard={renderProfileCard}
                  onViewAll={() => handleViewAllCategory('all')}
                  viewAllText="View All Profiles"
                  maxPreview={8}
                />
              )}

              {/* View All Page - Only show when NOT in discovery mode */}
              {selectedFilter !== 'discovery' && (
                <>
                  {/* Back Button */}
                  <div className="mb-6">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleBackToAll}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <ArrowLeft className="size-4 mr-2" />
                      Back to Discovery
                    </Button>
                  </div>

                  {/* Page Header */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {selectedFilter === 'premium' && <Crown className="size-8 text-yellow-500" />}
                        {selectedFilter === 'new' && <Sparkles className="size-8 text-pink-500" />}
                        {selectedFilter === 'all' && <Users className="size-8 text-blue-500" />}
                        <div>
                          <h1 className="text-3xl font-bold">
                            {selectedFilter === 'premium' && 'Premium Members'}
                            {selectedFilter === 'new' && 'New Profiles'}
                            {selectedFilter === 'verified' && 'Verified Profiles'}
                            {selectedFilter === 'nearby' && 'Nearby Matches'}
                            {selectedFilter === 'all' && 'All Profiles'}
                          </h1>
                          {/* Hide profile count on mobile, show on desktop */}
                          <p className="hidden md:block text-gray-600 mt-1">
                            {safePagination.total} {safePagination.total === 1 ? 'profile' : 'profiles'} found
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Profile Grid */}
                  <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 auto-rows-max' : 'space-y-4'}>
                    {categorizedProfiles.all.map((profile) => renderProfileCard(profile, profile.isPremium || profile.activePlan))}
                  </div>
                </>
              )}

              {/* Pagination - Only show when NOT in discovery mode */}
              {selectedFilter !== 'discovery' && safePagination && safePagination.totalPages > 1 && (
                <div className="flex flex-col items-center gap-4 mt-8">
                  {/* Page info - hide total count on mobile */}
                  <div className="text-sm text-gray-600">
                    <span className="hidden md:inline">Showing page {safePagination.currentPage} of {safePagination.totalPages} ({safePagination.total} total profiles)</span>
                    <span className="md:hidden">Page {safePagination.currentPage} of {safePagination.totalPages}</span>
                  </div>
                  
                  {/* Pagination controls */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={prevPage}
                      disabled={safePagination.currentPage === 1}
                      className="px-4"
                    >
                      <ChevronLeft className="size-4 mr-1" />
                      Previous
                    </Button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(safePagination.totalPages, 5) }, (_, i) => {
                        const pageNumber = i + 1;
                        return (
                          <Button
                            key={pageNumber}
                            variant={safePagination.currentPage === pageNumber ? "default" : "outline"}
                            onClick={() => goToPage(pageNumber)}
                            className={`px-3 ${safePagination.currentPage === pageNumber ? 'bg-pink-500 hover:bg-pink-600 text-white' : ''}`}
                          >
                            {pageNumber}
                          </Button>
                        );
                      })}
                      {safePagination.totalPages > 5 && (
                        <>
                          <span className="px-2 text-gray-500">...</span>
                          <Button
                            variant={safePagination.currentPage === safePagination.totalPages ? "default" : "outline"}
                            onClick={() => goToPage(safePagination.totalPages)}
                            className={`px-3 ${safePagination.currentPage === safePagination.totalPages ? 'bg-pink-500 hover:bg-pink-600 text-white' : ''}`}
                          >
                            {safePagination.totalPages}
                          </Button>
                        </>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      onClick={nextPage}
                      disabled={safePagination.currentPage === safePagination.totalPages}
                      className="px-4"
                    >
                      Next
                      <ChevronRight className="size-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {categorizedProfiles.all.length === 0 && !loading && (
                <div className="text-center py-12">
                  <Users className="size-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No matches found</h3>
                  <p className="text-gray-500 mb-4">Try adjusting your filters to see more profiles</p>
                  <Button onClick={handleResetFilters} className="bg-pink-500 hover:bg-pink-600 text-white">
                    Reset Filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Sidebar - Desktop Only */}
      <aside className="hidden xl:block w-80 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0">
        <div className="p-4 space-y-6">
          <ProfileSidebarSections 
            suggestedProfiles={suggestedProfiles}
            featuredProfiles={featuredProfiles}
            onViewProfile={onViewProfile}
          />
        </div>
      </aside>
    </div>
  );
}